import type * as React from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import Modal from '@cloudscape-design/components/modal';
import SpaceBetween from '@cloudscape-design/components/space-between';

type Props = {
  visible: boolean;
  title: string;
  confirmLabel: string;
  cancelLabel?: string;
  /** Carrega o spinner no botão de confirmação e bloqueia o cancelar. */
  loading?: boolean;
  /** Desabilita o botão de confirmação (validação pendente). */
  confirmDisabled?: boolean;
  onConfirm: () => void;
  onDismiss: () => void;
  children?: React.ReactNode;
};

/**
 * Wrapper canônico de confirmação do admin (Sprint 39) — substitui o
 * `ConfirmDialog` do shadcn. Usa o `Modal` do Cloudscape com rodapé
 * cancelar/confirmar. Confirmação destrutiva continua proibida via
 * `window.confirm` (`feedback_no_window_confirm`).
 *
 * Nota: o Cloudscape não tem variante de botão "destructive" (vermelha); o
 * peso destrutivo é comunicado pelo conteúdo (ex.: <Alert type="warning">).
 *
 * Origem: sem issue — commit da2203b1f, 2026-06-08 (migração de Addons para
 * Cloudscape, 39-E).
 *
 * Contrato: controlado pelo caller (`visible`, `onConfirm`, `onDismiss`); com
 * `loading`, o cancelar fica desabilitado e Esc/X/clique fora são ignorados,
 * para a ação em voo não ser abandonada no meio. Não executa nada sozinho.
 *
 * Falhas conhecidas:
 *  - até 2026-10-25 15:52 +0100 (#1021, 2ª auditoria): só o botão Cancelar
 *    respeitava `loading`; Esc, o X e o clique no overlay chamavam `onDismiss`
 *    com a chamada em voo. Desde que o erro passou a ser `Alert` DENTRO do
 *    confirm (o toast ficava atrás do overlay, #174), a falha que chegava depois
 *    caía num modal já fechado e ninguém a via.
 *
 * Histórico:
 *  - 2026-10-25 15:52 +0100 — (#1021) `onDismiss` ignorado durante `loading`.
 *
 * Usado por: as confirmações do admin — `UsersPage`, `ApiKeysPage`,
 * `NotificationsBroadcastPage`, `ModuleCatalogImportPage`, PSP, entidades
 * vendedoras, países, membros, responsáveis, modelos de etiqueta, contas de
 * plataforma, cortesia e os diálogos de billing (lista completa:
 * `rg -l AdminConfirm apps/admin/src`).
 */
export function AdminConfirm({
  visible,
  title,
  confirmLabel,
  cancelLabel,
  loading = false,
  confirmDisabled = false,
  onConfirm,
  onDismiss,
  children,
}: Props): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <Modal
      visible={visible}
      onDismiss={(): void => {
        // #1021 (2ª auditoria): com a ação em voo, fechar esconderia a falha (ver "Falhas conhecidas").
        if (!loading) onDismiss();
      }}
      header={title}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onDismiss} disabled={loading}>
              {cancelLabel ?? t('common.cancel', { defaultValue: 'Cancelar' })}
            </Button>
            <Button
              variant="primary"
              onClick={onConfirm}
              loading={loading}
              disabled={confirmDisabled}
            >
              {confirmLabel}
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      {children}
    </Modal>
  );
}
